import { useEffect, useMemo, useState } from "react";

/** Default options for catalog grids (app-wide). */
export const GRID_PAGE_SIZE_OPTIONS = [20, 24, 30, 36, 40, 48, 60] as const;

interface UseGridPaginationOptions {
  pageSizeOptions?: readonly number[];
  defaultPageSize?: number;
}

/**
 * Client-side pagination for catalog grids (search `query` resets to page 1).
 * Supports page-specific page size sets when layouts differ.
 */
export function useGridPagination<T>(
  filteredItems: T[],
  query: string,
  options?: UseGridPaginationOptions,
) {
  const available = options?.pageSizeOptions?.length
    ? options.pageSizeOptions
    : GRID_PAGE_SIZE_OPTIONS;
  const initial = options?.defaultPageSize ?? available[0];
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(initial);

  const safePageSize = available.includes(pageSize) ? pageSize : available[0];
  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / safePageSize));

  useEffect(() => {
    setPage(1);
  }, [query, safePageSize]);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const paginatedItems = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * safePageSize;
    return filteredItems.slice(start, start + safePageSize);
  }, [filteredItems, page, safePageSize, totalPages]);

  const rangeStart = totalCount === 0 ? 0 : (Math.min(page, totalPages) - 1) * safePageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(Math.min(page, totalPages) * safePageSize, totalCount);

  return {
    page,
    setPage,
    pageSize: safePageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems,
    rangeStart,
    rangeEnd,
  };
}
